import * as React from "react"
import { Waypoint } from "react-waypoint"
import { ThemeCss } from "gatsby-interface"
import Loading from "@modules/ui/components/Loading"
import { buildLogs as text } from "@modules/locales/default.js"
import { EmptyState } from "gatsby-interface"
import { EmptyStateGraphic } from "@modules/ui/components/EmptyStateGraphic"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { useBuildSrrLogs } from "../../ssr/hooks/useBuildRssLogs"
import { SeeRecentLogsAlert } from "./SeeRecentLogsAlert"
import { BuildSrrLogsItem } from "./BuildSrrLogsItem"

export type BuildSsrLogsProps = {
  buildId: string
  siteId: string
}

export function BuildSsrLogs({ buildId, siteId }: BuildSsrLogsProps) {
  const topAnchor = React.useRef<HTMLDivElement | null>(null)

  const [initialRecentTimestamp, setInitialRecentTimestamp] = React.useState<
    number
  >(0)

  React.useEffect(() => {
    if (!initialRecentTimestamp && logs?.[0]?.timestamp) {
      const recentLogTimestamp = new Date(logs[0].timestamp).getTime()
      setInitialRecentTimestamp(recentLogTimestamp)
    }
  })

  // for unknonw reason React does not properly reacts to the Apollo cache update
  // in cases where a group of new logs come through ssrLogCreated subscription
  // last log does not appear on the list, only after re-loading the page
  // this is a hack to force the component to re-render every time a new log is
  // dynamically added to the list
  const [_, setRecentDynamicallyAddedLogId] = React.useState<number>(0) // eslint-disable-line

  const [
    logs,
    { loading, error, seeRecentLogs, setSeeRecentLogs },
  ] = useBuildSrrLogs(buildId, siteId, setRecentDynamicallyAddedLogId)

  const clearAlert = () => {
    setSeeRecentLogs(false)
  }

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  if (loading) {
    return <Loading bufferSize="padded" />
  }

  return (
    <React.Fragment>
      {!logs || logs?.length === 0 ? (
        <EmptyState
          graphic={<EmptyStateGraphic />}
          heading={text.headers.noLogsFound}
          text=""
          variant="WITH_BACKGROUND"
        />
      ) : (
        <div css={containerCss}>
          <div id="top" ref={topAnchor} />
          <Waypoint onEnter={clearAlert} />

          {seeRecentLogs && <SeeRecentLogsAlert anchor={topAnchor} />}

          <div role="log" css={boxCss}>
            <ol aria-label="" css={itemsCss}>
              {logs.map((item, idx) => {
                const nextLogInvocationId = logs[idx + 1]
                  ? logs[idx + 1].invocationId
                  : null

                return (
                  <BuildSrrLogsItem
                    key={item.id}
                    log={item}
                    nextLogInvocationId={nextLogInvocationId}
                    initialRecentTimestamp={initialRecentTimestamp}
                  />
                )
              })}
            </ol>
          </div>
        </div>
      )}
    </React.Fragment>
  )
}

/* styles */

const containerCss: ThemeCss = theme => ({
  position: `relative`,
})

const boxCss: ThemeCss = theme => ({
  backgroundColor: theme.colors.grey[80],
  color: theme.colors.white,
  fontFamily: theme.fonts.monospace,
  fontSize: theme.fontSizes[1],
  lineHeight: theme.lineHeights.default,
  position: `relative`,
  fontVariantLigatures: `none`,
  paddingTop: theme.space[4],
  paddingBottom: theme.space[1],
})

const itemsCss: ThemeCss = theme => ({
  listStyle: `none`,
  margin: 0,
  padding: 0,
  position: `relative`,
})
